import { graphqlRequest } from "../graphqlClient";
import type {
  BaseForecastViewConnection,
  BaseForecastViewFilterInput,
} from "../models/baseForecastView";
import type { ForecastMetricsViewNode } from "../models/forecastMetricsView";

export interface BaseForecastViewSortInput {
  field: string;
  order: "Ascending" | "Descending";
}

interface ForecastMetricsViewResponse {
  forecast: {
    forecastMetricsView: ForecastMetricsViewNode | null;
  };
}

/**
 * Fetch the headline forecast metrics of the latest completed run for a period
 * (vw_ForecastMetrics) via GraphQL.
 */
export async function getForecastMetricsGraphql(
  periodKey = "90d",
): Promise<ForecastMetricsViewNode | null> {
  const query = `
    query ForecastMetricsView($periodKey: String) {
      forecast {
        forecastMetricsView(periodKey: $periodKey) {
          periodKey
          startingCash
          minCash
          minCashDate
          endingCash
          coverageMonths
          isNoBurn
          avgMonthlyInflow
          avgMonthlyOutflow
          confidenceScore
        }
      }
    }
  `;
  const data = await graphqlRequest<ForecastMetricsViewResponse>(query, {
    periodKey,
  });
  return data.forecast.forecastMetricsView;
}

interface BaseForecastViewListResponse {
  forecast: {
    baseForecastViewList: BaseForecastViewConnection;
  };
}

/**
 * Fetch the ground-truth base forecast (latest completed daily run) via GraphQL.
 * Defaults to ascending date order so the timeline reads chronologically.
 */
export async function getBaseForecastGraphql(
  filter?: BaseForecastViewFilterInput,
  first?: number,
  after?: string,
  sort: BaseForecastViewSortInput[] = [{ field: "Date", order: "Ascending" }],
): Promise<BaseForecastViewConnection> {
  const query = `
    query BaseForecastViewList($filter: BaseForecastViewFilterInput, $first: Int, $after: String, $sort: [BaseForecastViewSortSortByInput!]) {
      forecast {
        baseForecastViewList(filter: $filter, first: $first, after: $after, sort: $sort) {
          edges {
            cursor
            node {
              id
              periodKey
              forecastRunResultId
              date
              openingBalance
              balance
              incoming
              outgoing
              confidenceScore
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
      }
    }
  `;
  const data = await graphqlRequest<BaseForecastViewListResponse>(query, {
    filter,
    first,
    after,
    sort,
  });
  return data.forecast.baseForecastViewList;
}
